/**
 * Les **guichets du réseau** — la lecture de gestion et ses gestes (V90, 2026-10-01).
 *
 * Demandé par le porteur : « l'écran Réseau guichets doit pouvoir ajouter, et toutes
 * les actions ». Le tableau de liquidité ne lit que les guichets ACTIFS : un guichet
 * suspendu y disparaissait, et ne pouvait plus être rouvert depuis l'écran qui l'avait
 * suspendu. Cette lecture rend TOUS les guichets, avec leur statut.
 *
 * Les gestes passent par la route des actions du socle : le motif est saisi, le
 * journal d'audit écrit par le serveur. Réactiver et fermer attendent une SECONDE
 * signature — l'issue le dit (`approvalRequestId`), et l'écran aussi.
 */

import type { ApiClient } from './api-client.js';
import type { ActionOutcome } from './liquidity.js';
import { networkPath, operatorActionsPath } from './outlet-operators.js';

/** Le motif des lectures de cet écran. */
export const outletsReason = 'gestion-des-guichets-du-reseau';

/** Les statuts d'un guichet, tels que la base les nomme. */
export const outletStatuses = ['ACTIF', 'SUSPENDU', 'FERME'] as const;

/** Un guichet, tel que le serveur le rend. */
export interface NetworkOutlet {
  readonly outletId: string;
  readonly outletCode: string;
  readonly businessName: string;
  readonly category: string;
  readonly region: string | null;
  readonly addressLine: string | null;
  readonly latitude: string | null;
  readonly longitude: string | null;
  readonly status: string;
  /** `null` : le seuil du réseau s'applique. */
  readonly floatLowThreshold: number | null;
  readonly cashHighThreshold: number | null;
  readonly activatedAt: string | null;
  readonly activeOperators: number;
}

/** Ce qu'un guichet déclare de lui-même — le formulaire, champ pour champ. */
export interface OutletDescription {
  readonly businessName: string;
  readonly category: string;
  readonly region: string;
  readonly addressLine: string;
  readonly latitude: string;
  readonly longitude: string;
  readonly floatLowThreshold: string;
  readonly cashHighThreshold: string;
}

/** Les champs de la description, dans l'ordre du formulaire. */
export const outletDescriptionFields: readonly (keyof OutletDescription)[] = [
  'businessName',
  'category',
  'region',
  'addressLine',
  'latitude',
  'longitude',
  'floatLowThreshold',
  'cashHighThreshold',
];

/** Les gestes de statut d'un guichet. */
export const outletStatusActions = {
  suspend: 'OUTLET_SUSPEND',
  reactivate: 'OUTLET_REACTIVATE',
  close: 'OUTLET_CLOSE',
} as const;

/** Les gestes de statut d'un compte de guichet. */
export const operatorStatusActions = {
  suspend: 'OUTLET_OPERATOR_SUSPEND',
  reactivate: 'OUTLET_OPERATOR_REACTIVATE',
  revoke: 'OUTLET_OPERATOR_REVOKE',
} as const;

export type OutletStatusGesture = keyof typeof outletStatusActions;
export type OperatorStatusGesture = keyof typeof operatorStatusActions;

/** Lit tous les guichets, quel que soit leur statut. */
export async function fetchNetworkOutlets(
  client: ApiClient,
  reason: string = outletsReason,
): Promise<readonly NetworkOutlet[]> {
  const parametres = new URLSearchParams({ reason });
  return client.request<readonly NetworkOutlet[]>(
    `${networkPath}/outlets?${parametres.toString()}`,
  );
}

/** Ouvre un guichet, et son compte au grand livre. */
export async function createOutlet(
  client: ApiClient,
  outletCode: string,
  description: OutletDescription,
  reason: string,
): Promise<ActionOutcome> {
  return agir(client, 'OUTLET_CREATE', outletCode.trim().toUpperCase(), reason, {
    ...description,
  });
}

/** Remplace la fiche d'un guichet. */
export async function updateOutlet(
  client: ApiClient,
  outletCode: string,
  description: OutletDescription,
  reason: string,
): Promise<ActionOutcome> {
  return agir(client, 'OUTLET_UPDATE', outletCode, reason, { ...description });
}

/** Suspend, réactive ou ferme un guichet. */
export async function changeOutletStatus(
  client: ApiClient,
  outletCode: string,
  gesture: OutletStatusGesture,
  reason: string,
): Promise<ActionOutcome> {
  return agir(client, outletStatusActions[gesture], outletCode, reason, {});
}

/** Suspend, réactive ou révoque un compte de guichet. */
export async function changeOperatorStatus(
  client: ApiClient,
  operatorId: string,
  gesture: OperatorStatusGesture,
  reason: string,
): Promise<ActionOutcome> {
  return agir(client, operatorStatusActions[gesture], operatorId, reason, {});
}

/** La fiche d'un guichet, mise en forme de formulaire. */
export function descriptionOf(outlet: NetworkOutlet): OutletDescription {
  return {
    businessName: outlet.businessName,
    category: outlet.category,
    region: outlet.region ?? '',
    addressLine: outlet.addressLine ?? '',
    latitude: outlet.latitude ?? '',
    longitude: outlet.longitude ?? '',
    floatLowThreshold:
      outlet.floatLowThreshold === null ? '' : String(outlet.floatLowThreshold),
    cashHighThreshold:
      outlet.cashHighThreshold === null ? '' : String(outlet.cashHighThreshold),
  };
}

async function agir(
  client: ApiClient,
  action: string,
  entityRef: string,
  reason: string,
  payload: Readonly<Record<string, string>>,
): Promise<ActionOutcome> {
  return client.request<ActionOutcome>(`${operatorActionsPath}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entityRef, reason, payload }),
  });
}
